import React, { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { useSnackbar } from 'notistack'
import { Panel, Toggle } from 'src/components/app-ui'
import {
  AddMenu,
  ColorField,
  ContrastWarning,
  ListRow,
  RowMenu,
  SortableList,
  move,
  slugify,
} from './parts'
import { ChangeLabelDialog, LinkDialog, NewPageDialog } from './dialogs'
import useSignInLabel from './useSignInLabel'
import { slugForRename } from 'src/utils/portal-page-slug'
import BrandFields from './brand-fields'
import useStyles from './styles'
import { PortalHeaderBar, headerTheme, menuRows } from './portal-bars'

export { headerTheme, menuRows }

export const HEADER_BG_SWATCHES = [
  '#FFFFFF',
  '#F4F6F8',
  '#002E47',
  '#015FFB',
  '#111827',
]
export const TEXT_SWATCHES = [
  '#000000',
  '#FFFFFF',
  '#002E47',
  '#637381',
  '#015FFB',
]

export const newLinkId = () =>
  `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

/** Portal › Navigation: pages & menu, header colors and behavior. */
const NavigationTab = ({
  portal,
  update,
  createPage,
  updatePage,
  deletePage,
  host,
  shopName,
  workspaceLogo,
  openEditor,
}) => {
  const classes = useStyles()
  const [linkDialog, setLinkDialog] = useState(null) // {} new, or a link
  const [newPageOpen, setNewPageOpen] = useState(false)
  const [labelDialog, setLabelDialog] = useState(null)
  const [signInLabel, saveSignInLabel] = useSignInLabel()
  const { enqueueSnackbar } = useSnackbar()

  const pages = portal.pages || []
  const pageById = id => pages.find(page => page.id === id)
  const home = pages.find(page => page.type === 'home')
  const account = pages.find(page => page.type === 'account')
  const rows = menuRows(portal)

  const saveRows = (next, message) =>
    update(current => ({ ...current, menuItems: next }), message)

  const saveLink = ({ label, url, newTab }) => {
    const id = linkDialog?.id
    saveRows(
      id
        ? rows.map(row =>
            row.id === id ? { ...row, label, url, newTab } : row,
          )
        : [
            ...rows,
            {
              type: 'link',
              id: newLinkId(),
              label,
              url,
              newTab,
              visible: true,
            },
          ],
      id ? 'Link updated' : `"${label}" added to the menu`,
    )
    setLinkDialog(null)
  }

  const removeLink = link => {
    saveRows(
      rows.filter(row => row.id !== link.id),
      `"${link.label}" removed from the menu`,
    )
    setLinkDialog(null)
  }

  const headerBg = portal.headerBackgroundColor || '#FFFFFF'
  const headerText = portal.headerTextColor || '#000000'
  const setHeader = patch => update(current => ({ ...current, ...patch }))
  // Slider values not saved yet, so the preview follows the slider.
  const [draft, setDraft] = useState({})
  useEffect(() => setDraft({}), [portal])

  // Change label: a custom page or the catalog moves to the address its new
  // label gives (the catalog keeps answering at /catalog too); Home, My
  // account and Sign in keep theirs.
  const changePageLabel = page => {
    const movable = page.type === 'custom' || page.type === 'catalog'
    const currentSlug = page.slug || (page.type === 'catalog' ? 'catalog' : '')
    setLabelDialog({
      label: page.name,
      urlFor: movable ? name => slugify(name) || currentSlug : null,
      fixedUrl: movable ? null : page.path,
      onSave: async name => {
        const slug = movable ? await slugForRename(page, name) : undefined
        const saved = await updatePage(page.id, { name, slug })
        if (saved) setLabelDialog(null)
      },
    })
  }
  const changeLinkLabel = link =>
    setLabelDialog({
      label: link.label,
      onSave: label => {
        saveRows(
          rows.map(row => (row.id === link.id ? { ...row, label } : row)),
          'Label changed',
        )
        setLabelDialog(null)
      },
    })
  const changeSignInLabel = () =>
    setLabelDialog({
      label: signInLabel,
      fixedUrl: '/login',
      onSave: label =>
        saveSignInLabel(label).then(
          () => {
            enqueueSnackbar('Saved', { variant: 'success' })
            setLabelDialog(null)
          },
          () =>
            enqueueSnackbar('Could not save the label.', { variant: 'error' }),
        ),
    })

  const builtInRow = (page, icon, note, info) => (
    <ListRow
      icon={icon}
      label={page.name}
      note={note}
      info={info}
      target={page.path}
      onClick={() => openEditor(page)}
      right={
        <>
          <span className={classes.toggleSlot} />
          <span className={classes.menuSlot}>
            <RowMenu
              editLabel="Edit page"
              onEdit={() => openEditor(page)}
              onChangeLabel={() => changePageLabel(page)}
            />
          </span>
        </>
      }
    />
  )

  return (
    <div className={classes.page}>
      <Panel className={classes.panel}>
        <div className={classes.previewHead}>
          <Icon icon="tabler:eye" width={16} height={16} />
          <span>Preview · shown at the top of every portal page</span>
        </div>
        <div className={classes.previewStage}>
          <div className={classes.footerFrame}>
            <PortalHeaderBar
              portal={portal}
              logo={workspaceLogo}
              shopName={shopName}
              logoSize={draft.headerLogoSize}
              signInLabel={signInLabel}
            />
            <div className={classes.footerStripes} />
          </div>
        </div>
      </Panel>

      <div className={classes.grid}>
        <Panel className={classes.panel}>
          <div className={classes.listHead}>
            <div style={{ minWidth: 0 }}>
              <div className={classes.cardTitle}>Pages &amp; menu</div>
              <div className={classes.listSub}>
                Drag to reorder. The toggle shows or hides an item in the menu.
              </div>
            </div>
            <AddMenu
              options={[
                {
                  icon: 'tabler:file-plus',
                  color: '#015FFB',
                  title: 'New page',
                  description: 'Build it with blocks in the editor.',
                  onClick: () => setNewPageOpen(true),
                },
                {
                  icon: 'tabler:link',
                  color: '#637381',
                  title: 'External link',
                  description: 'Point to your blog, website or help center.',
                  onClick: () => setLinkDialog({}),
                },
              ]}
            />
          </div>

          {home && builtInRow(home, 'tabler:home', 'Logo link')}

          <SortableList
            items={rows}
            getKey={row =>
              row.type === 'link' ? `l-${row.id}` : `p-${row.pageId}`
            }
            onMove={(from, to) => saveRows(move(rows, from, to))}
            renderRow={(row, index, dragProps) => {
              const toggle = (
                <span className={classes.toggleSlot}>
                  <Toggle
                    checked={row.visible}
                    title="Show in menu"
                    onChange={visible =>
                      saveRows(
                        rows.map((r, i) =>
                          i === index ? { ...r, visible } : r,
                        ),
                      )
                    }
                  />
                </span>
              )
              if (row.type === 'link') {
                return (
                  <ListRow
                    dragProps={dragProps}
                    icon="tabler:external-link"
                    label={row.label}
                    target={row.url}
                    onClick={() => setLinkDialog(row)}
                    right={
                      <>
                        {toggle}
                        <span className={classes.menuSlot}>
                          <RowMenu
                            editLabel="Edit link"
                            onEdit={() => setLinkDialog(row)}
                            onChangeLabel={() => changeLinkLabel(row)}
                            onRemove={() => removeLink(row)}
                          />
                        </span>
                      </>
                    }
                  />
                )
              }
              const page = pageById(row.pageId)
              return (
                <ListRow
                  dragProps={dragProps}
                  icon="tabler:file-text"
                  label={page.name}
                  target={page.path}
                  onClick={() => openEditor(page)}
                  right={
                    <>
                      {toggle}
                      <span className={classes.menuSlot}>
                        <RowMenu
                          editLabel="Edit page"
                          onEdit={() => openEditor(page)}
                          onChangeLabel={() => changePageLabel(page)}
                          onRemove={
                            page.type === 'custom'
                              ? () => deletePage(page.id)
                              : null
                          }
                        />
                      </span>
                    </>
                  }
                />
              )
            }}
          />

          {account &&
            builtInRow(
              account,
              'tabler:users',
              'After sign in',
              'Learners see this page only after they sign in. It shows their progress and certificates.',
            )}

          <ListRow
            icon="tabler:key"
            label={signInLabel}
            target="/login"
            right={
              <>
                <span className={classes.toggleSlot}>
                  <Toggle
                    checked={portal.showSignIn}
                    title="Show in menu"
                    onChange={showSignIn => setHeader({ showSignIn })}
                  />
                </span>
                <span className={classes.menuSlot}>
                  <RowMenu onChangeLabel={changeSignInLabel} />
                </span>
              </>
            }
          />
        </Panel>

        <div className={classes.column}>
          <Panel className={classes.card}>
            <div className={classes.cardTitle}>Header</div>
            <ColorField
              label="Background color"
              value={headerBg}
              swatches={HEADER_BG_SWATCHES}
              onChange={headerBackgroundColor =>
                setHeader({ headerBackgroundColor })
              }
            />
            <ColorField
              label="Text color"
              value={headerText}
              swatches={TEXT_SWATCHES}
              onChange={headerTextColor => setHeader({ headerTextColor })}
            />
            <ContrastWarning
              background={headerBg}
              text={headerText}
              what="Menu"
              onFix={headerTextColor => setHeader({ headerTextColor })}
            />
            <div className={classes.divider} />
            <BrandFields
              kind="header"
              portal={portal}
              onChange={setHeader}
              onDraft={setDraft}
            />
            <div className={classes.divider} />
            <div className={classes.settingRow}>
              <div>
                <div className={classes.fieldTitle}>Sticky header</div>
                <div className={classes.settingDesc}>
                  Header stays visible while scrolling.
                </div>
              </div>
              <Toggle
                checked={portal.stickyHeader}
                onChange={stickyHeader => setHeader({ stickyHeader })}
              />
            </div>
            <div className={classes.divider} />
            <div className={classes.settingRow}>
              <div>
                <div className={classes.fieldTitle}>Show header</div>
                <div className={classes.settingDesc}>
                  Turn off to hide the header on portal pages.
                </div>
              </div>
              <Toggle
                checked={portal.showHeader !== false}
                onChange={showHeader => setHeader({ showHeader })}
              />
            </div>
            <div className={classes.divider} />
            <div className={classes.settingRow}>
              <div>
                <div className={classes.fieldTitle}>Show breadcrumbs</div>
                <div className={classes.settingDesc}>
                  Shows the path to the current page above the title on path
                  and course pages.
                </div>
              </div>
              <Toggle
                checked={portal.showBreadcrumbs !== false}
                onChange={showBreadcrumbs =>
                  update(
                    current => ({ ...current, showBreadcrumbs }),
                    showBreadcrumbs ? 'Breadcrumbs shown' : 'Breadcrumbs hidden',
                  )
                }
              />
            </div>
          </Panel>
        </div>
      </div>

      {linkDialog && (
        <LinkDialog
          link={linkDialog.id ? linkDialog : null}
          subtitle="Adds a link to your menu that points outside the portal."
          onClose={() => setLinkDialog(null)}
          onSave={saveLink}
          onRemove={() => removeLink(linkDialog)}
        />
      )}

      {labelDialog && (
        <ChangeLabelDialog
          label={labelDialog.label}
          urlPrefix={labelDialog.urlFor ? `${host}/` : null}
          urlFor={labelDialog.urlFor}
          fixedUrl={labelDialog.fixedUrl}
          onClose={() => setLabelDialog(null)}
          onSave={labelDialog.onSave}
        />
      )}

      {newPageOpen && (
        <NewPageDialog
          host={host}
          showInMenu
          onClose={() => setNewPageOpen(false)}
          onCreate={data =>
            createPage(data).then(saved => {
              if (!saved) return
              setNewPageOpen(false)
              const page = saved.pages.find(p => p.slug === data.slug)
              if (page) openEditor(page)
            })
          }
        />
      )}
    </div>
  )
}

export default NavigationTab
